import React, { useState } from "react";

// ============================================================
// ERUE FESTIVAL SUITE — LEGAL PAGES (DROP 52A)
// Terms & Conditions | Privacy Policy | EULA
// Cookie Policy | Refund Policy | Disclaimer
// Nigerian Law: NDPA 2023 | CAMA 2020
// © Digitrax Finteca Consulting
// ============================================================

type LegalDoc =
  | "terms"
  | "privacy"
  | "eula"
  | "cookies"
  | "refund"
  | "disclaimer";

interface LegalPagesProps {
  initialDoc?: LegalDoc;
  onClose?: () => void;
  embedded?: boolean;
}

const EFFECTIVE_DATE = "14 October 2026";
const ORG_NAME       = "ERUE Festival of Uzairue Kingdom";
const ORG_ENTITY     = "Uzairue Kingdom Cultural Festival Committee";
const REGULATOR      = "Corporate Affairs Commission (CAC), Nigeria";
const CAMA_REF       = "Companies and Allied Matters Act 2020";
const NDPA_REF       = "Nigeria Data Protection Act 2023 (NDPA)";
const NITDA_REF      = "National Information Technology Development Agency (NITDA)";
const SUPPORT_EMAIL  = "support@digitraxfinteca.com";
const FESTIVAL_URL   = "https://erueuzairue.org";
const FESTIVAL_DATES = "December 24\u201330, 2026";
const VENUE          = "Palace of the Ogieneni of Uzairue Kingdom, Jattu-Uzairue, Etsako West LGA, Edo State, Nigeria";
const PAYSTACK_NOTE  = "Payment processing is handled by Paystack (paystack.com), a PCI-DSS compliant payment processor licensed by the Central Bank of Nigeria (CBN).";

const docs: Record<LegalDoc, { title: string; icon: string }> = {
  terms:      { title: "Terms & Conditions",  icon: "\u2696\ufe0f" },
  privacy:    { title: "Privacy Policy",       icon: "🔒" },
  eula:       { title: "End User Licence",     icon: "📜" },
  cookies:    { title: "Cookie Policy",        icon: "🍪" },
  refund:     { title: "Refund Policy",        icon: "💰" },
  disclaimer: { title: "Disclaimer",           icon: "⚠️" },
};

// ── SHARED STYLES ──────────────────────────────────────────────────────
const S = {
  wrap:    { background: "#070D0A", minHeight: "100vh", color: "#EDE8DC", fontFamily: "'Plus Jakarta Sans', sans-serif" },
  inner:   { maxWidth: 860, margin: "0 auto", padding: "32px 24px" },
  h1:      { fontFamily: "'Cinzel', serif", color: "#C9A84C", fontSize: 26, marginBottom: 8 },
  h2:      { fontFamily: "'Cinzel', serif", color: "#C9A84C", fontSize: 18, marginTop: 32, marginBottom: 10, borderBottom: "1px solid #1C2E22", paddingBottom: 6 },
  h3:      { color: "#E2C97E", fontSize: 15, marginTop: 20, marginBottom: 6 },
  p:       { color: "#D4CFC5", fontSize: 14, lineHeight: 1.8, marginBottom: 12 },
  ul:      { color: "#D4CFC5", fontSize: 14, lineHeight: 1.9, paddingLeft: 20, marginBottom: 12 },
  badge:   { display: "inline-block", background: "#0F5132", color: "#C9A84C", padding: "3px 10px", borderRadius: 4, fontSize: 11, marginBottom: 16, border: "1px solid #1C2E22" },
  card:    { background: "#0E1812", border: "1px solid #1C2E22", borderRadius: 8, padding: "16px 20px", marginBottom: 16 },
  warn:    { background: "#1A0A0A", border: "1px solid #EF4444", borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: "#FCA5A5", fontSize: 13 },
  info:    { background: "#071218", border: "1px solid #1C3A4A", borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: "#93C5FD", fontSize: 13 },
  tab:     (active: boolean) => ({
    padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, marginBottom: 6,
    background: active ? "#0F5132" : "#0E1812",
    color: active ? "#C9A84C" : "#9CA3AF",
    border: active ? "1px solid #C9A84C" : "1px solid #1C2E22",
    textAlign: "left" as const, width: "100%",
  }),
};

// ════════════════════════════════════════════════════════════════════
// TERMS & CONDITIONS
// ════════════════════════════════════════════════════════════════════
const TermsContent: React.FC = () => (
  <div>
    <div style={S.badge}>Effective: {EFFECTIVE_DATE} | Governed by Nigerian Law</div>
    <h2 style={S.h2}>1. Introduction & Acceptance</h2>
    <p style={S.p}>
      These Terms and Conditions (&ldquo;Terms&rdquo;) govern your access to and use of the
      official <strong>{ORG_NAME}</strong> digital portal (&ldquo;Platform&rdquo;) operated by
      the <strong>{ORG_ENTITY}</strong>, facilitated by Digitrax Finteca Consulting
      (&ldquo;Technical Partner&rdquo;). By accessing or using the Platform, you agree to be
      bound by these Terms. If you do not agree, you must not use the Platform.
    </p>
    <p style={S.p}>
      These Terms are governed by and construed in accordance with the laws of the
      Federal Republic of Nigeria, including the {NDPA_REF} and the {CAMA_REF}.
    </p>

    <h2 style={S.h2}>2. Platform Description</h2>
    <p style={S.p}>
      The Platform provides digital services for the <strong>{ORG_NAME}</strong>, including
      but not limited to: event registration, cultural programme scheduling, festival store
      purchases, raffle ticket acquisition, contestant voting, news publishing, community
      portals for the 22 villages and component communities of Uzairue Kingdom, diaspora
      engagement, financial management tools for authorised staff, and sponsorship
      facilitation.
    </p>
    <p style={S.p}>
      The Festival is scheduled to take place <strong>{FESTIVAL_DATES}</strong> at the{" "}
      <strong>{VENUE}</strong>.
    </p>

    <h2 style={S.h2}>3. User Accounts & Registration</h2>
    <ul style={S.ul}>
      <li>You must provide accurate, complete, and current information when registering.</li>
      <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
      <li>You must immediately notify us of any unauthorised use of your account.</li>
      <li>One person may not create multiple accounts to circumvent voting or raffle limits.</li>
      <li>Accounts created for staff purposes are non-transferable and role-restricted.</li>
      <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
    </ul>

    <h2 style={S.h2}>4. Payments & Financial Transactions</h2>
    <p style={S.p}>{PAYSTACK_NOTE}</p>
    <ul style={S.ul}>
      <li>All prices are displayed in Nigerian Naira (NGN) unless stated otherwise.</li>
      <li>Payments are processed securely via Paystack. We do not store card details.</li>
      <li>Receipts are issued electronically to the email address provided at checkout.</li>
      <li>Disputed transactions should be reported to {SUPPORT_EMAIL} within 7 days.</li>
      <li>All donations are voluntary contributions to the festival and are non-refundable unless stated in our Refund Policy.</li>
    </ul>

    <h2 style={S.h2}>5. Raffle Rules & Fairness</h2>
    <ul style={S.ul}>
      <li>All raffle draws are conducted using cryptographically secure random selection.</li>
      <li>Each draw generates a publicly verifiable SHA-256 audit hash.</li>
      <li>Raffle tickets are non-transferable and non-refundable after purchase.</li>
      <li>Winners are selected impartially from all eligible paid tickets.</li>
      <li>Employees of Digitrax Finteca Consulting and their immediate families are excluded from participation.</li>
      <li>Prize values are as stated. No cash alternatives are offered unless explicitly stated.</li>
      <li>Results are final and binding.</li>
    </ul>

    <h2 style={S.h2}>6. Voting Rules</h2>
    <ul style={S.ul}>
      <li>Voting is limited to one vote per authenticated account per voting period.</li>
      <li>Vote purchases via Paystack are subject to rate limits to ensure fairness.</li>
      <li>Vote manipulation, use of bots, scripts, or multiple accounts is strictly prohibited.</li>
      <li>The organising committee reserves the right to disqualify fraudulent votes.</li>
    </ul>

    <h2 style={S.h2}>7. Intellectual Property</h2>
    <p style={S.p}>
      All content on this Platform — including text, images, logos, graphics, audio, video,
      and software — is the intellectual property of the {ORG_ENTITY} or its licensors.
      Unauthorised reproduction, distribution, or modification is prohibited.
    </p>
    <p style={S.p}>
      The Uzairue Kingdom name, royal symbols, and Ogieneni title are protected cultural
      designations. Their use without express written permission is prohibited.
    </p>

    <h2 style={S.h2}>8. User Conduct</h2>
    <p style={S.p}>You agree not to:</p>
    <ul style={S.ul}>
      <li>Upload false, defamatory, or offensive content.</li>
      <li>Attempt to gain unauthorised access to any part of the Platform.</li>
      <li>Interfere with Platform security or integrity.</li>
      <li>Use the Platform for any unlawful purpose under Nigerian law.</li>
      <li>Impersonate any official of Uzairue Kingdom, the BOT, CWC, LOC, or Digitrax Finteca.</li>
    </ul>

    <h2 style={S.h2}>9. Limitation of Liability</h2>
    <p style={S.p}>
      To the maximum extent permitted by Nigerian law, the {ORG_ENTITY} and Digitrax Finteca
      Consulting shall not be liable for any indirect, incidental, special, or consequential
      damages arising from your use of the Platform, including loss of data, revenue, or
      goodwill.
    </p>

    <h2 style={S.h2}>10. Changes to Terms</h2>
    <p style={S.p}>
      We reserve the right to modify these Terms at any time. Changes will be posted on this
      page with an updated effective date. Continued use of the Platform constitutes acceptance
      of the revised Terms.
    </p>

    <h2 style={S.h2}>11. Governing Law & Dispute Resolution</h2>
    <p style={S.p}>
      These Terms are governed by the laws of the Federal Republic of Nigeria.
      Any disputes shall first be attempted to be resolved through good-faith negotiation.
      Failing that, disputes shall be submitted to the jurisdiction of Nigerian courts.
    </p>

    <h2 style={S.h2}>12. Contact</h2>
    <div style={S.card}>
      <p style={{ ...S.p, marginBottom: 4 }}><strong style={{ color: "#C9A84C" }}>{ORG_ENTITY}</strong></p>
      <p style={{ ...S.p, marginBottom: 4 }}>c/o Digitrax Finteca Consulting</p>
      <p style={{ ...S.p, marginBottom: 4 }}>Email: <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#C9A84C" }}>{SUPPORT_EMAIL}</a></p>
      <p style={{ ...S.p, marginBottom: 0 }}>Website: <a href={FESTIVAL_URL} style={{ color: "#C9A84C" }}>{FESTIVAL_URL}</a></p>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════
// PRIVACY POLICY
// ════════════════════════════════════════════════════════════════════
const PrivacyContent: React.FC = () => (
  <div>
    <div style={S.badge}>NDPA 2023 Compliant | Effective: {EFFECTIVE_DATE}</div>
    <p style={S.p}>
      This Privacy Policy explains how the <strong>{ORG_ENTITY}</strong>, facilitated by
      Digitrax Finteca Consulting (&ldquo;we&rdquo;, &ldquo;us&rdquo;), collects, uses,
      stores, and protects your personal data in compliance with the{" "}
      <strong>{NDPA_REF}</strong> and {NITDA_REF} guidelines.
    </p>

    <h2 style={S.h2}>1. Data Controller</h2>
    <div style={S.card}>
      <p style={{ ...S.p, marginBottom: 4 }}><strong>{ORG_ENTITY}</strong></p>
      <p style={{ ...S.p, marginBottom: 4 }}>Technical Partner: Digitrax Finteca Consulting</p>
      <p style={{ ...S.p, marginBottom: 0 }}>Contact: <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#C9A84C" }}>{SUPPORT_EMAIL}</a></p>
    </div>

    <h2 style={S.h2}>2. Data We Collect</h2>
    <h3 style={S.h3}>2.1 Information You Provide</h3>
    <ul style={S.ul}>
      <li><strong>Identity data:</strong> First name, last name, title, display name</li>
      <li><strong>Contact data:</strong> Email address, phone number</li>
      <li><strong>Account data:</strong> Password (hashed — never stored in plain text), role, department</li>
      <li><strong>Registration data:</strong> Event registration details, village affiliation</li>
      <li><strong>Payment data:</strong> Transaction references (card data processed solely by Paystack — we receive no card details)</li>
      <li><strong>Community data:</strong> Village community, diaspora location</li>
      <li><strong>Communications:</strong> Messages, support requests, news submissions</li>
    </ul>

    <h3 style={S.h3}>2.2 Automatically Collected Data</h3>
    <ul style={S.ul}>
      <li>IP address and login timestamps (for security purposes)</li>
      <li>Device type and browser (for platform optimisation)</li>
      <li>Session activity and page navigation (aggregated, not tracked individually)</li>
    </ul>

    <h2 style={S.h2}>3. Legal Basis for Processing (NDPA 2023)</h2>
    <ul style={S.ul}>
      <li><strong>Consent:</strong> Registration, marketing communications, voting</li>
      <li><strong>Contract:</strong> Event registration, store purchases, raffle participation</li>
      <li><strong>Legitimate Interest:</strong> Security monitoring, fraud prevention, audit logs</li>
      <li><strong>Legal Obligation:</strong> CAMA 2020 financial records, tax obligations</li>
    </ul>

    <h2 style={S.h2}>4. How We Use Your Data</h2>
    <ul style={S.ul}>
      <li>To create and manage your account</li>
      <li>To process event registrations, store orders, raffle tickets, and votes</li>
      <li>To send transactional emails (account creation, password changes, receipts)</li>
      <li>To communicate festival news, schedules, and important announcements</li>
      <li>To maintain financial records as required by {CAMA_REF}</li>
      <li>To detect and prevent fraud, abuse, and security threats</li>
      <li>To improve the Platform and user experience</li>
    </ul>

    <h2 style={S.h2}>5. Data Sharing</h2>
    <p style={S.p}>We do not sell your personal data. We share data only with:</p>
    <ul style={S.ul}>
      <li><strong>Paystack:</strong> Payment processing (PCI-DSS certified)</li>
      <li><strong>Resend:</strong> Transactional email delivery</li>
      <li><strong>Cloudinary:</strong> Image storage and delivery</li>
      <li><strong>Contabo VPS:</strong> Secure server hosting (Germany/EU data centre)</li>
      <li><strong>Nigerian authorities:</strong> When required by law or court order</li>
    </ul>

    <h2 style={S.h2}>6. Data Retention</h2>
    <ul style={S.ul}>
      <li>Account data: Retained for the duration of your account plus 2 years</li>
      <li>Financial records: 7 years (CAMA 2020 / FIRS requirements)</li>
      <li>Audit logs: 3 years</li>
      <li>Event registration data: 2 years post-event</li>
      <li>Deleted accounts: Anonymised within 90 days of deletion request</li>
    </ul>

    <h2 style={S.h2}>7. Your Rights Under NDPA 2023</h2>
    <ul style={S.ul}>
      <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
      <li><strong>Right to Rectification:</strong> Correct inaccurate data</li>
      <li><strong>Right to Erasure:</strong> Request deletion (subject to legal retention obligations)</li>
      <li><strong>Right to Object:</strong> Object to processing based on legitimate interest</li>
      <li><strong>Right to Data Portability:</strong> Receive your data in a portable format</li>
      <li><strong>Right to Withdraw Consent:</strong> At any time, without penalty</li>
    </ul>
    <p style={S.p}>
      To exercise any right, email <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#C9A84C" }}>{SUPPORT_EMAIL}</a>.
      We will respond within 30 days as required by the NDPA 2023.
    </p>

    <h2 style={S.h2}>8. Security Measures</h2>
    <ul style={S.ul}>
      <li>All passwords are hashed using bcrypt (cost factor 12)</li>
      <li>All data transmission encrypted via TLS 1.2/1.3 (SSL)</li>
      <li>JWT authentication with short-lived access tokens</li>
      <li>Rate limiting on all authentication endpoints</li>
      <li>Webhook signature validation (HMAC-SHA512) for payment events</li>
      <li>Server access restricted to SSH key authentication</li>
      <li>Regular security audits conducted by Digitrax Finteca Consulting</li>
    </ul>

    <h2 style={S.h2}>9. Cookies</h2>
    <p style={S.p}>
      We use essential session cookies for authentication. See our{" "}
      <strong>Cookie Policy</strong> for full details.
    </p>

    <h2 style={S.h2}>10. Complaints</h2>
    <p style={S.p}>
      If you are dissatisfied with how we handle your data, you may lodge a complaint with
      the <strong>National Information Technology Development Agency (NITDA)</strong> at{" "}
      <a href="https://nitda.gov.ng" style={{ color: "#C9A84C" }}>nitda.gov.ng</a>.
    </p>
  </div>
);

// ════════════════════════════════════════════════════════════════════
// EULA
// ════════════════════════════════════════════════════════════════════
const EulaContent: React.FC = () => (
  <div>
    <div style={S.badge}>End User Licence Agreement | Effective: {EFFECTIVE_DATE}</div>
    <div style={S.warn}>
      <strong>IMPORTANT:</strong> Please read this EULA carefully before using the ERUE
      Festival Platform. By accessing the Platform, you agree to be bound by this licence.
    </div>

    <h2 style={S.h2}>1. Licence Grant</h2>
    <p style={S.p}>
      The <strong>{ORG_ENTITY}</strong>, facilitated by Digitrax Finteca Consulting
      (&ldquo;Licensor&rdquo;), grants you a limited, non-exclusive, non-transferable,
      revocable licence to access and use the ERUE Festival Platform (&ldquo;Software&rdquo;)
      solely for the purposes of participating in the {ORG_NAME} and its associated activities.
    </p>

    <h2 style={S.h2}>2. Restrictions</h2>
    <p style={S.p}>You may not:</p>
    <ul style={S.ul}>
      <li>Copy, modify, distribute, or create derivative works of the Platform</li>
      <li>Reverse engineer, decompile, or disassemble any part of the Platform</li>
      <li>Remove or alter any proprietary notices, branding, or labels</li>
      <li>Use the Platform for any purpose other than festival participation</li>
      <li>Sublicence, sell, rent, or transfer your access rights to any third party</li>
      <li>Use automated tools, bots, or scripts to access Platform features</li>
      <li>Circumvent any security, access control, or rate-limiting mechanisms</li>
    </ul>

    <h2 style={S.h2}>3. Intellectual Property Ownership</h2>
    <p style={S.p}>
      The Platform, including all software, design, content, and functionality, remains the
      exclusive property of the {ORG_ENTITY} and Digitrax Finteca Consulting. This EULA does
      not transfer any ownership rights to you.
    </p>

    <h2 style={S.h2}>4. Platform as Cultural Service</h2>
    <p style={S.p}>
      This Platform is provided as a cultural and community service by the{" "}
      {ORG_ENTITY}, a non-profit cultural committee registered under the{" "}
      {CAMA_REF} and regulated by the {REGULATOR}. It is not a commercial software product.
    </p>

    <h2 style={S.h2}>5. Staff & Administrative Users</h2>
    <p style={S.p}>
      Users granted staff roles (Board of Trustees, CWC, LOC, Financial Hub, etc.) are
      bound by additional obligations of confidentiality and fiduciary duty. Unauthorised
      disclosure of financial data, voter information, or personal data of platform users
      constitutes a breach of this EULA and may attract legal liability under Nigerian law.
    </p>

    <h2 style={S.h2}>6. Termination</h2>
    <p style={S.p}>
      This licence terminates automatically if you breach any provision of this EULA.
      Upon termination, you must cease all use of the Platform. The Licensor may also
      terminate or suspend access at any time with or without notice.
    </p>

    <h2 style={S.h2}>7. Disclaimer of Warranties</h2>
    <p style={S.p}>
      The Platform is provided &ldquo;as is&rdquo; without warranty of any kind, express
      or implied. The Licensor does not warrant that the Platform will be error-free,
      uninterrupted, or free of security vulnerabilities. Use is at your own risk.
    </p>

    <h2 style={S.h2}>8. Festival-Specific Terms</h2>
    <ul style={S.ul}>
      <li>This licence is valid from the date of account creation through 28 February 2027.</li>
      <li>Post-festival, certain features (news archive, gallery) remain accessible in read-only mode.</li>
      <li>Financial, payroll, and administrative modules remain active for post-festival reporting.</li>
      <li>The Platform may be retired or archived after the post-festival period.</li>
    </ul>

    <h2 style={S.h2}>9. Governing Law</h2>
    <p style={S.p}>
      This EULA is governed by the laws of the Federal Republic of Nigeria.
      Any disputes shall be resolved in Nigerian courts.
    </p>
  </div>
);

// ════════════════════════════════════════════════════════════════════
// COOKIE POLICY
// ════════════════════════════════════════════════════════════════════
const CookieContent: React.FC = () => (
  <div>
    <div style={S.badge}>Cookie Policy | Effective: {EFFECTIVE_DATE}</div>
    <p style={S.p}>
      This Cookie Policy explains how the <strong>{ORG_NAME}</strong> Platform uses cookies
      and similar technologies. We use only essential cookies necessary for Platform operation.
    </p>

    <h2 style={S.h2}>1. What Are Cookies?</h2>
    <p style={S.p}>
      Cookies are small text files stored on your device when you visit a website. They help
      the website remember your preferences and maintain your session across pages.
    </p>

    <h2 style={S.h2}>2. Cookies We Use</h2>
    <div style={S.card}>
      <p style={{ ...S.p, color: "#C9A84C", fontWeight: "bold", marginBottom: 8 }}>Essential Cookies Only</p>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, color: "#D4CFC5" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #1C2E22" }}>
            <th style={{ textAlign: "left", padding: "6px 8px", color: "#C9A84C" }}>Cookie</th>
            <th style={{ textAlign: "left", padding: "6px 8px", color: "#C9A84C" }}>Purpose</th>
            <th style={{ textAlign: "left", padding: "6px 8px", color: "#C9A84C" }}>Duration</th>
          </tr>
        </thead>
        <tbody>
          {[
            ["erue_session", "Authentication session token", "8 hours"],
            ["erue_refresh", "Session refresh token", "7 days"],
            ["erue_pwa", "PWA install prompt state", "30 days"],
          ].map(([name, purpose, duration]) => (
            <tr key={name} style={{ borderBottom: "1px solid #1C2E22" }}>
              <td style={{ padding: "6px 8px", fontFamily: "monospace", color: "#E2C97E" }}>{name}</td>
              <td style={{ padding: "6px 8px" }}>{purpose}</td>
              <td style={{ padding: "6px 8px" }}>{duration}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 style={S.h2}>3. Cookies We Do NOT Use</h2>
    <ul style={S.ul}>
      <li>No advertising or tracking cookies</li>
      <li>No third-party analytics cookies (e.g., Google Analytics)</li>
      <li>No social media tracking pixels</li>
      <li>No cross-site tracking technologies</li>
    </ul>

    <h2 style={S.h2}>4. Managing Cookies</h2>
    <p style={S.p}>
      You can control cookies through your browser settings. Note that disabling essential
      cookies will prevent you from logging in to the Platform.
    </p>

    <h2 style={S.h2}>5. Third-Party Services</h2>
    <p style={S.p}>
      Paystack (payment processing) may set its own cookies during checkout. Please refer to
      Paystack&apos;s Privacy Policy at <a href="https://paystack.com/privacy" style={{ color: "#C9A84C" }}>paystack.com/privacy</a>.
    </p>
  </div>
);

// ════════════════════════════════════════════════════════════════════
// REFUND POLICY
// ════════════════════════════════════════════════════════════════════
const RefundContent: React.FC = () => (
  <div>
    <div style={S.badge}>Refund Policy | Effective: {EFFECTIVE_DATE}</div>
    <div style={S.info}>
      All refund requests must be submitted to{" "}
      <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#C9A84C" }}>{SUPPORT_EMAIL}</a>{" "}
      within the applicable time window stated below.
    </div>

    <h2 style={S.h2}>1. Festival Store Purchases</h2>
    <ul style={S.ul}>
      <li>Digital items (tickets, passes): <strong>Non-refundable</strong> after download/use</li>
      <li>Physical merchandise: Refundable within <strong>7 days</strong> of receipt if item is defective or not as described</li>
      <li>Change of mind: No refunds on physical items once dispatched</li>
      <li>Defective items will be replaced or refunded within 14 business days</li>
    </ul>

    <h2 style={S.h2}>2. Event Registration</h2>
    <ul style={S.ul}>
      <li>Cancellations more than <strong>14 days before the event</strong>: Full refund minus processing fee (5%)</li>
      <li>Cancellations <strong>7–14 days</strong> before: 50% refund</li>
      <li>Cancellations <strong>less than 7 days</strong> before: No refund</li>
      <li>If the event is cancelled by organisers: Full refund within 14 business days</li>
    </ul>

    <h2 style={S.h2}>3. Raffle Tickets</h2>
    <ul style={S.ul}>
      <li>Raffle tickets are <strong>non-refundable</strong> once purchased</li>
      <li>In the event of a system error causing double charging, full refund within 5 business days</li>
      <li>Proof of double charge (Paystack reference numbers) must be provided</li>
    </ul>

    <h2 style={S.h2}>4. Voting Packs</h2>
    <ul style={S.ul}>
      <li>Voting packs are <strong>non-refundable</strong> once credits are applied to a contestant</li>
      <li>Unused voting credits due to system error: Refund or credit within 5 business days</li>
    </ul>

    <h2 style={S.h2}>5. Donations & Sponsorships</h2>
    <ul style={S.ul}>
      <li>Voluntary donations are <strong>non-refundable</strong></li>
      <li>Sponsorship packages: Refundable within <strong>30 days</strong> of payment if activation has not begun</li>
      <li>Partially activated sponsorships: Pro-rata refund at committee discretion</li>
    </ul>

    <h2 style={S.h2}>6. How to Request a Refund</h2>
    <ol style={{ ...S.ul, listStyleType: "decimal" }}>
      <li>Email <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#C9A84C" }}>{SUPPORT_EMAIL}</a></li>
      <li>Subject line: &ldquo;REFUND REQUEST &mdash; [Order/Transaction Reference]&rdquo;</li>
      <li>Include: Full name, email used for purchase, Paystack reference, reason for refund</li>
      <li>We will acknowledge within 2 business days and resolve within 14 business days</li>
    </ol>

    <h2 style={S.h2}>7. Refund Method</h2>
    <p style={S.p}>
      All approved refunds are returned to the original payment method via Paystack.
      Bank transfer times may vary (typically 3–5 business days after approval).
    </p>
  </div>
);

// ════════════════════════════════════════════════════════════════════
// DISCLAIMER
// ════════════════════════════════════════════════════════════════════
const DisclaimerContent: React.FC = () => (
  <div>
    <div style={S.badge}>Disclaimer | Effective: {EFFECTIVE_DATE}</div>
    <div style={S.warn}>
      <strong>Please read this disclaimer carefully.</strong> By using the Platform,
      you acknowledge and accept the limitations described herein.
    </div>

    <h2 style={S.h2}>1. General Information</h2>
    <p style={S.p}>
      The information provided on the ERUE Festival Platform is for general informational and
      cultural purposes only. While we strive for accuracy, we make no representations or
      warranties of any kind, express or implied, about the completeness, accuracy,
      reliability, or suitability of the information on the Platform.
    </p>

    <h2 style={S.h2}>2. Festival Programme</h2>
    <p style={S.p}>
      Festival schedules, programme of events, speaker lineups, and venue details are subject
      to change without notice. The {ORG_ENTITY} reserves the right to modify, postpone,
      or cancel events due to circumstances beyond its control, including but not limited to
      security concerns, weather, or force majeure events.
    </p>

    <h2 style={S.h2}>3. External Links</h2>
    <p style={S.p}>
      The Platform may contain links to external websites. We have no control over the content
      or availability of those sites and accept no responsibility for them.
    </p>

    <h2 style={S.h2}>4. Financial Information</h2>
    <p style={S.p}>
      Financial figures, budget summaries, and fundraising totals displayed on the Platform
      are for informational purposes and are subject to audit. They do not constitute audited
      financial statements. The {ORG_ENTITY} operates as a non-profit cultural committee
      under the {CAMA_REF}.
    </p>

    <h2 style={S.h2}>5. Cultural & Royal Content</h2>
    <p style={S.p}>
      Information about the Ogieneni of Uzairue Kingdom, royal traditions, and community
      history is provided with respect and accuracy to the best of our knowledge. Any errors
      brought to our attention will be corrected promptly.
    </p>

    <h2 style={S.h2}>6. Technical Platform</h2>
    <p style={S.p}>
      The Platform is built and maintained by Digitrax Finteca Consulting as a technical
      service provider. Digitrax Finteca Consulting is not responsible for the content of
      the festival programme, prize fulfilment, or the conduct of the festival itself.
      These remain the responsibility of the {ORG_ENTITY}.
    </p>

    <h2 style={S.h2}>7. No Professional Advice</h2>
    <p style={S.p}>
      Nothing on this Platform constitutes legal, financial, medical, or any other professional
      advice. Always seek qualified professional advice for matters of importance.
    </p>

    <h2 style={S.h2}>8. Liability Limitation</h2>
    <p style={S.p}>
      To the fullest extent permitted by Nigerian law, the {ORG_ENTITY} and Digitrax Finteca
      Consulting shall not be liable for any loss or damage arising from your use of the
      Platform, whether direct, indirect, incidental, or consequential.
    </p>
  </div>
);

// ════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════
const contentMap: Record<LegalDoc, React.FC> = {
  terms:      TermsContent,
  privacy:    PrivacyContent,
  eula:       EulaContent,
  cookies:    CookieContent,
  refund:     RefundContent,
  disclaimer: DisclaimerContent,
};

const LegalPages: React.FC<LegalPagesProps> = ({
  initialDoc = "terms",
  onClose,
  embedded = false,
}) => {
  const [active, setActive] = useState<LegalDoc>(initialDoc);
  const Content = contentMap[active];

  return (
    <div style={embedded ? {} : S.wrap}>
      <div style={embedded ? {} : S.inner}>

        {/* Header */}
        {!embedded && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
            <div>
              <h1 style={S.h1}>Legal & Policies</h1>
              <p style={{ ...S.p, marginBottom: 0, color: "#6B7280" }}>
                {ORG_NAME} &mdash; {FESTIVAL_DATES}
              </p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                style={{ background: "none", border: "1px solid #1C2E22", color: "#9CA3AF", cursor: "pointer", borderRadius: 6, padding: "6px 14px", fontSize: 13 }}
              >
                Close
              </button>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>

          {/* Sidebar */}
          <div style={{ width: 200, flexShrink: 0 }}>
            <p style={{ color: "#6B7280", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Documents</p>
            {(Object.keys(docs) as LegalDoc[]).map(key => (
              <button
                key={key}
                onClick={() => setActive(key)}
                style={S.tab(active === key)}
              >
                {docs[key].icon} {docs[key].title}
              </button>
            ))}
            <div style={{ marginTop: 24, padding: "12px 16px", background: "#070D0A", border: "1px solid #1C2E22", borderRadius: 8 }}>
              <p style={{ color: "#6B7280", fontSize: 11, marginBottom: 6 }}>Questions?</p>
              <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#C9A84C", fontSize: 12, wordBreak: "break-all" }}>
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
              <span style={{ fontSize: 24 }}>{docs[active].icon}</span>
              <h1 style={{ ...S.h1, marginBottom: 0 }}>{docs[active].title}</h1>
            </div>
            <Content />
            <div style={{ marginTop: 32, padding: "12px 16px", background: "#070D0A", border: "1px solid #1C2E22", borderRadius: 8, fontSize: 12, color: "#4B5563", textAlign: "center" }}>
              &copy; {new Date().getFullYear()} Digitrax Finteca Consulting &mdash; digitraxfinteca.com
              &nbsp;|&nbsp; All rights reserved &nbsp;|&nbsp; Uzairue Kingdom, Nigeria
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LegalPages;
export type { LegalDoc };
